import React, { useState } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface IssueType {
  id: string;
  label: string;
  icon: string;
}

interface PriorityLevel {
  id: string;
  label: string;
  color: string;
  description: string;
}

interface ContactMethod {
  id: string;
  label: string;
  icon: string;
  description: string;
}

const ContactSupport: React.FC = () => {
  const colors = getThemeColors('dark');

  const [selectedIssue, setSelectedIssue] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [contactMethod, setContactMethod] = useState('email');
  const [loading, setLoading] = useState(false);

  const issueTypes: IssueType[] = [
    { id: 'account', label: 'Account Issues', icon: '👤' },
    { id: 'payment', label: 'Payment Problems', icon: '💳' },
    { id: 'chama', label: 'Chama Related', icon: '👥' },
    { id: 'technical', label: 'Technical Issues', icon: '🐛' },
    { id: 'security', label: 'Security Concerns', icon: '🛡️' },
    { id: 'other', label: 'Other', icon: '❓' },
  ];

  const priorityLevels: PriorityLevel[] = [
    { id: 'low', label: 'Low', color: colors.success, description: 'General inquiry' },
    { id: 'medium', label: 'Medium', color: colors.warning, description: 'Issue affecting usage' },
    { id: 'high', label: 'High', color: colors.error, description: 'Urgent issue' },
  ];

  const contactMethods: ContactMethod[] = [
    { id: 'email', label: 'Email', icon: '📧', description: 'Response within 24 hours' },
    { id: 'phone', label: 'Phone Call', icon: '📞', description: 'Business hours only' },
    { id: 'whatsapp', label: 'WhatsApp', icon: '💬', description: 'Quick response' },
  ];

  const handleSubmitTicket = async () => {
    if (!selectedIssue) {
      alert('Please select an issue type.');
      return;
    }

    if (!description.trim()) {
      alert('Please describe your issue.');
      return;
    }

    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const supportRequest = {
        category: selectedIssue,
        subject: `${issueTypes.find(t => t.id === selectedIssue)?.label || 'Support Request'}`,
        description: description.trim(),
        priority,
        userInfo: {
          userId: 'user123',
          email: 'user@example.com',
          firstName: 'John',
          lastName: 'Doe',
        },
      };

      console.log('Creating support request:', supportRequest);

      alert(`Support request submitted successfully!\n\nRequest ID: #${Date.now()}\n\nWe'll get back to you within 24 hours.`);

      // Reset form
      setSelectedIssue('');
      setDescription('');
      setPriority('medium');
      setContactMethod('email');

    } catch (error) {
      console.error('Failed to create support request:', error);
      alert('Failed to create support request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDirectContact = (method: string) => {
    switch (method) {
      case 'email':
        const email = 'support@vaultke.com';
        const subject = `VaultKe Support - ${issueTypes.find(t => t.id === selectedIssue)?.label || 'General Inquiry'}`;
        const body = `Issue Type: ${selectedIssue}\nPriority: ${priority}\n\nDescription:\n${description}\n\nUser ID: user123\nApp Version: 1.0.0`;

        window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        break;

      case 'phone':
        const phoneNumber = '+254700000000';
        if (window.confirm(`Call ${phoneNumber}?\n\nBusiness Hours: Mon-Fri 8AM-6PM EAT`)) {
          window.open(`tel:${phoneNumber}`);
        }
        break;

      case 'whatsapp':
        const whatsappNumber = '254700000000';
        const message = `Hello VaultKe Support!\n\nIssue: ${selectedIssue}\nDescription: ${description}\nUser ID: user123`;

        window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`);
        break;
    }
  };

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    issueTypesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: spacing.md,
    },
    issueType: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    issueTypeSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
      transform: 'scale(1.02)',
    },
    issueTypeIcon: {
      fontSize: 24,
      marginRight: spacing.md,
    },
    issueTypeText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    descriptionInput: {
      width: '100%',
      padding: spacing.lg,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.background,
      color: colors.text,
      fontSize: typography.fontSize.base,
      minHeight: 120,
      outline: 'none',
      resize: 'vertical',
    },
    priorityGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
    },
    priorityLevel: {
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    priorityLevelSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
    },
    priorityContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    priorityLabel: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
    },
    priorityDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    contactMethodsGrid: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
    },
    contactMethod: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing.lg,
      borderRadius: borderRadius.lg,
      border: `2px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    contactMethodSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '10',
    },
    contactMethodIcon: {
      fontSize: 24,
      marginRight: spacing.md,
    },
    contactMethodContent: {
      flex: 1,
    },
    contactMethodLabel: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    contactMethodDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    actionButtons: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.lg,
      marginTop: spacing.xl,
    },
    submitButton: {
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      cursor: 'pointer',
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    directContactButton: {
      padding: `${spacing.lg} ${spacing.xl}`,
      borderRadius: borderRadius.lg,
      backgroundColor: 'transparent',
      color: colors.primary,
      border: `2px solid ${colors.primary}`,
      cursor: 'pointer',
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      textAlign: 'center',
      ...getShadowStyle('md'),
      transition: 'all 0.3s ease',
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Contact Support"
        subtitle="Get personalized help from our support team"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>💬</div>
            <h1 style={styles.headerTitle}>Contact Support</h1>
            <p style={styles.headerSubtitle}>Get personalized help from our support team</p>
          </div>

          {/* Issue Type Selection */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>What can we help you with?</h2>
            <div style={styles.issueTypesGrid}>
              {issueTypes.map((issue) => (
                <div
                  key={issue.id}
                  style={{
                    ...styles.issueType,
                    ...(selectedIssue === issue.id ? styles.issueTypeSelected : {}),
                  }}
                  onClick={() => setSelectedIssue(issue.id)}
                >
                  <span style={styles.issueTypeIcon}>{issue.icon}</span>
                  <span style={styles.issueTypeText}>{issue.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Description */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Describe your issue</h2>
            <textarea
              style={styles.descriptionInput}
              placeholder="Please provide as much detail as possible..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </div>

          {/* Priority Level */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Priority Level</h2>
            <div style={styles.priorityGrid}>
              {priorityLevels.map((level) => (
                <div
                  key={level.id}
                  style={{
                    ...styles.priorityLevel,
                    ...(priority === level.id ? styles.priorityLevelSelected : {}),
                  }}
                  onClick={() => setPriority(level.id)}
                >
                  <div style={styles.priorityContent}>
                    <span style={styles.priorityLabel}>{level.label}</span>
                    <span style={styles.priorityDescription}>{level.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Method */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>How should we contact you?</h2>
            <div style={styles.contactMethodsGrid}>
              {contactMethods.map((method) => (
                <div
                  key={method.id}
                  style={{
                    ...styles.contactMethod,
                    ...(contactMethod === method.id ? styles.contactMethodSelected : {}),
                  }}
                  onClick={() => setContactMethod(method.id)}
                >
                  <span style={styles.contactMethodIcon}>{method.icon}</span>
                  <div style={styles.contactMethodContent}>
                    <div style={styles.contactMethodLabel}>{method.label}</div>
                    <div style={styles.contactMethodDescription}>{method.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionButtons}>
            <button
              style={{
                ...styles.submitButton,
                ...(loading ? styles.submitButtonDisabled : {}),
              }}
              onClick={handleSubmitTicket}
              disabled={loading}
            >
              {loading ? 'Creating Support Ticket...' : 'Create Support Ticket'}
            </button>

            <button
              style={styles.directContactButton}
              onClick={() => handleDirectContact(contactMethod)}
            >
              Contact Directly via {contactMethods.find(m => m.id === contactMethod)?.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSupport;